/**
 * We fetch the values from the Bluesky UX, they are then stored lowercase
 *
 * When we display the values on the recommendation card, we join the strings
 * and capitalize only the first letter.
 *
 * When we undo, we compare initial values against lower cased values on the
 * UX to ensure we can find the right ones.
 */
import { BlueskySettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { BlueskyRecommendationKey, StageType } from '$extensionSrc/utils/enums'

import { AUTOMATION_STAGE_STATUSES } from '../genericAutomationStageStatuses'
import * as blueskyUrls from './blueskyUrls'
import { StageDef } from '../automationMachineInterfaces'
import { wait } from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import {
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'

// Define expected values, and map to-from internal representation

const _labelValuePairs: [string, string][] = [
  ['Allow anyone to reply', 'everybody'],
  ['Disable replies entirely', 'nobody'],
  ['Allow your followers to reply', 'your followers'],
  ['Allow people you follow to reply', 'users you follow'],
  ['Allow people you mention to reply', 'mentioned users'],
]

const _labelToValueMap = Object.fromEntries(_labelValuePairs)
const _valueToLabelMap = Object.fromEntries(
  _labelValuePairs.map(([a, b]) => [b, a]),
)

export function getValueFromLabel(label: string): string | undefined {
  if (label.toLocaleLowerCase().startsWith('allow users in')) {
    const listName = /allow users in (.+) to reply/i.exec(label)[1]
    return `custom (${listName})`
  }

  return _labelToValueMap[label]
}

function getLabelFromValue(value: string): string | undefined {
  if (value.startsWith('custom')) {
    const listName = /custom \((.+)\)/i.exec(value)[0]
    return `Allow users in ${listName} to reply`
  }

  return _valueToLabelMap[value]
}

const TARGET_VALUES: string[] = [
  'mentioned users',
  'users you follow',
  'your followers',
]

function toTitleCase(str: string): string {
  const excluded = new Set(['in'])

  return str.replace(/\w\S*/g, (text) => {
    if (excluded.has(text.toLocaleLowerCase())) {
      return text.toLocaleLowerCase()
    }
    return (
      text.charAt(0).toLocaleUpperCase() + text.substring(1).toLocaleLowerCase()
    )
  })
}

export function allowRepliesFromCurrentValueFn(
  values: string[],
): string | undefined {
  if (!values || !values?.length) {
    return undefined
  }

  function sortValuesForDisplay(a: string, b: string): number {
    const score = (x: string) => {
      switch (x) {
        case 'everybody':
          return 0
        case 'nobody':
          return 1
        case 'mentioned users':
          return 2
        case 'users you follow':
          return 3
        case 'your followers':
          return 4
        default:
          return 6
      }
    }

    return score(a) - score(b)
  }

  values.sort(sortValuesForDisplay)

  return values.map(toTitleCase).join(', ')
}

export function allowRepliesFromIsFullfilledFn(value: string[]): boolean {
  if (!value || !value.length) {
    return false
  }

  return !value.includes('everybody')
}

// Settings property functions
export const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, allowRepliesFromCurrentValueFn)

export const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, allowRepliesFromIsFullfilledFn)

export const getCantUndoFn = () => defaultCantUndoFn

export function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
export function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

enum StageKey {
  GATHER_SETTING_POST_INTERACTIONS = 'GATHER_SETTING_POST_INTERACTIONS',
  UPDATE_SETTING_POST_INTERACTIONS = 'UPDATE_SETTING_POST_INTERACTIONS',
  UNDO_SETTING_POST_INTERACTIONS = 'UNDO_SETTING_POST_INTERACTIONS',
}

export async function getCheckedButtonLabels(): Promise<string[]> {
  await getElement<HTMLElement>(document, '//*[text() = "Who can reply"]')

  const showListsButton = evaluateXpath<HTMLDivElement>(
    document,
    '//*[@aria-label = "Show lists of users to select from"]',
  )[0]
  if (showListsButton) {
    showListsButton.click()
  }

  await wait(200)

  const checkedButtons = evaluateXpath<HTMLDivElement>(
    document,
    '//div[@aria-checked="true"]',
  )

  return checkedButtons.map((button) => button.ariaLabel)
}

export async function selectButtonsWithLabels(
  labels: string[],
  isDry: boolean,
): Promise<void> {
  await getElement<HTMLElement>(document, '//*[text() = "Who can reply"]')

  const showListsButton = evaluateXpath<HTMLDivElement>(
    document,
    '//*[@aria-label = "Show lists of users to select from"]',
  )[0]
  if (showListsButton) {
    showListsButton.click()
  }

  // Click buttons
  for (const label of labels) {
    const button = await getElement<HTMLButtonElement>(
      document,
      `//div[${getCaseInsensitiveXPathTextSelector('aria-label', label.toLowerCase())}]`,
    )
    await wait(200)
    if (button.ariaChecked === 'false') {
      button.click()
    }
  }

  // Save settings update
  const saveButton = await getElement<HTMLButtonElement>(
    document,
    `//button[${getCaseInsensitiveXPathTextSelector('aria-label', 'save')}]`,
  )

  if (!isDry) {
    await wait(500)
    saveButton.click()
  }
}

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTING_POST_INTERACTIONS,
    status: AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: blueskyUrls.postInteractionSettingsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const selectedSettings = await getCheckedButtonLabels()

      await updateStoredInitialValues(
        {
          [BlueskySettingKey.ALLOW_REPLIES_FROM]: selectedSettings
            .map(getValueFromLabel)
            .filter((x) => x),
        },
        BlueskyRecommendationKey.BLUESKY_POST_INTERACTIONS,
      )
    },
  },
]

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTING_POST_INTERACTIONS,
    status: AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: blueskyUrls.postInteractionSettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          BlueskySettingKey.ALLOW_REPLIES_FROM,
          getIsFulfilledFn,
        )
      ) {
        const labels = TARGET_VALUES.map(getLabelFromValue)
        await selectButtonsWithLabels(labels, isDry)
      }
    },
  },
]

export const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTING_POST_INTERACTIONS,
    status: AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: blueskyUrls.postInteractionSettingsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry, settingsSelected } = userContext

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          BlueskySettingKey.ALLOW_REPLIES_FROM,
          getIsFulfilledFn,
        )
      ) {
        const labels =
          initialValues[BlueskySettingKey.ALLOW_REPLIES_FROM].map(
            getLabelFromValue,
          )
        await selectButtonsWithLabels(labels, isDry)
      }
    },
  },
]
